'use client';

import { ArrowUp } from 'lucide-react';

export default function Footer() {
  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer className="relative bg-[#1A0E08] text-[#F8F1E4] py-16 md:py-20 mt-auto">
      {/* Top divider */}
      <div className="divider-gold absolute top-0 left-0 right-0" />

      <div className="container mx-auto px-6">
        {/* Main grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full border border-[#C9A961] flex items-center justify-center">
                <span className="font-display text-[#C9A961] text-xl font-bold tracking-wider">A</span>
              </div>
              <div className="flex flex-col leading-none">
                <span className="font-display text-[#F8F1E4] text-xl tracking-[0.3em] font-semibold">
                  ASMAO
                </span>
                <span className="font-serif-display text-[#C9A961] text-xs tracking-[0.4em] uppercase mt-1">
                  Style Cuir
                </span>
              </div>
            </div>
            <p className="text-[#F8F1E4]/70 font-serif-display font-light leading-relaxed max-w-md mb-6">
              Maison de maroquinerie artisanale française. Sacs, ceintures, portefeuilles et
              chaussures en cuir pleine fleur, façonnés à la main dans nos ateliers parisiens.
              Une pièce d'ASMAO n'est pas un accessoire — c'est un compagnon de vie.
            </p>
            <div className="text-[#C9A961] text-xs tracking-[0.3em] uppercase font-serif-display">
              Une Maison SMARTEST CAPITAL
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-display text-[#F8F1E4] text-base tracking-[0.2em] uppercase mb-5">
              Explorer
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="/boutique"
                  className="text-[#C9A961] hover:text-[#D4A017] transition-colors duration-300 text-sm font-serif-display tracking-wide font-semibold flex items-center gap-2"
                >
                  Boutique en Ligne
                  <span className="text-[9px] bg-[#C9A961] text-[#2B1810] px-1.5 py-0.5 rounded-full font-bold">
                    NEW
                  </span>
                </a>
              </li>
              {[
                { label: 'La Maison', href: '#maison' },
                { label: 'Notre Héritage', href: '#heritage' },
                { label: 'Collections', href: '#collections' },
                { label: "L'Atelier", href: '#atelier' },
                { label: 'Savoir-Faire', href: '#savoir-faire' },
                { label: 'Le Lexique', href: '#lexique' },
                { label: 'Salon Privé', href: '#salon-prive' },
                { label: 'Distinctions', href: '#distinctions' },
                { label: 'Galerie', href: '#galerie' },
                { label: 'Témoignages', href: '#temoignages' },
                { label: 'Contact', href: '#contact' },
              ].map((item) => (
                <li key={item.href}>
                  <button
                    onClick={() => {
                      const el = document.querySelector(item.href);
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-[#F8F1E4]/65 hover:text-[#C9A961] transition-colors duration-300 text-sm font-serif-display tracking-wide"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-[#F8F1E4] text-base tracking-[0.2em] uppercase mb-5">
              Nous Contacter
            </h4>
            <ul className="space-y-4 text-sm font-serif-display text-[#F8F1E4]/65">
              <li>
                <div className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase mb-1">
                  Boutique
                </div>
                Rue Clignancourt
                <br />
                75018 Paris, France
              </li>
              <li>
                <div className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase mb-1">
                  Horaires
                </div>
                Mar — Sam : 10h — 19h
              </li>
              <li>
                <div className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase mb-1">
                  Téléphone
                </div>
                +33 6 99 70 39 84
              </li>
              <li>
                <div className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase mb-1">
                  Email
                </div>
                asmao@edisem.fr
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="divider-gold w-full mb-8 opacity-50" />

        {/* Suppliers / Partners */}
        <div className="mb-10 p-6 border border-[#C9A961]/20 bg-[#2B1810]/40 rounded-sm">
          <div className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase font-serif-display mb-4 text-center">
            Nos Fournisseurs & Partenaires d'Excellence
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="text-center md:text-left">
              <div className="text-[#F8F1E4] text-sm font-serif-display font-semibold mb-1">
                Dallu-Ngaay-Chaussures
              </div>
              <div className="text-[#F8F1E4]/60 text-xs font-serif-display italic mb-2">
                Chaussures & Maroquinerie Artisanales
              </div>
              <a
                href="https://Ndiamsyl-peye.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C9A961] hover:text-[#D4A017] text-xs font-serif-display tracking-wide transition-colors"
              >
                Ndiamsyl-peye.com →
              </a>
            </div>
            <div className="text-center md:text-left">
              <div className="text-[#F8F1E4] text-sm font-serif-display font-semibold mb-1">
                SylvoPastoral-Academy
              </div>
              <div className="text-[#F8F1E4]/60 text-xs font-serif-display italic mb-2">
                Formation & Transmission du Savoir-Faire
              </div>
              <a
                href="https://Smartest-Capital.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C9A961] hover:text-[#D4A017] text-xs font-serif-display tracking-wide transition-colors"
              >
                Smartest-Capital.org →
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-xs">
          <div className="text-[#F8F1E4]/50 font-serif-display tracking-wide text-center md:text-left">
            © {new Date().getFullYear()} ASMAO STYLE CUIR · SMARTEST CAPITAL. Tous droits réservés.
            <br className="md:hidden" />
            <span className="hidden md:inline"> · </span>
            Maroquinerie d'exception fabriquée à Paris.
          </div>

          <div className="flex items-center gap-6 text-[#F8F1E4]/50 font-serif-display tracking-wide">
            <a href="#" className="hover:text-[#C9A961] transition-colors">Mentions légales</a>
            <a href="#" className="hover:text-[#C9A961] transition-colors">Confidentialité</a>
            <a href="#" className="hover:text-[#C9A961] transition-colors">CGV</a>
          </div>

          <button
            onClick={scrollTop}
            className="group flex items-center gap-2 text-[#C9A961] hover:text-[#D4A017] text-xs tracking-[0.25em] uppercase font-serif-display transition-colors"
            aria-label="Remonter en haut"
          >
            Haut de page
            <span className="w-9 h-9 rounded-full border border-[#C9A961]/40 flex items-center justify-center group-hover:border-[#C9A961] group-hover:bg-[#C9A961]/10 transition-all duration-500">
              <ArrowUp size={14} />
            </span>
          </button>
        </div>
      </div>
    </footer>
  );
}
